import { CommentOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import PermissionCheck from 'common/features/permission-check';
import Table from 'common/table/table';
import { ThemeHeader } from 'common/table/table-helper';
import { ToggleFilter, ToggleSort, Toolbar } from 'common/toolbar';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'ui';
import { withRouter } from 'utils/withRouter';

const AlertIcon = styled(ExclamationCircleOutlined)`
  color: ${p => p.theme.colors.alerts['1']};
  font-size: 1.2rem;
`;

const TicketIcon = styled(CommentOutlined)`
  color: ${p => p.theme.colors.alerts['3']};
  font-size: 1.2rem;
`;

const Wrapper = styled.div`
  background-color: #fff;
`;

const prefix = 'dashboard.equipmentsTable';

const EquipmentsTable = ({
  isLoading,
  equipments,
  intl,
  history,
  activeRow,
}) => {
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [toggleContractValue, setToggleContractValue] = useState('all');
  const [toggleCompetitorValue, setToggleCompetitorValue] = useState('all');
  const [toggleWarrantyValue, setToggleWarrantyValue] = useState('all');
  const [sortOrder, setSortOrder] = useState(() => {
    const previousSort = localStorage.getItem('equipmentTable-sort');
    const defaultSort = previousSort ?
      JSON.parse(previousSort) : { order: 'name', direction: 'ASC' };

    return defaultSort;
  });

  const toggleContract = [
    {
      text: intl.formatMessage({ id: `${prefix}.filter.ALL` }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.YES` }),
      value: 'yes',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.NO` }),
      value: 'no',
    },
  ];

  const toggleCompetitor = [
    {
      text: intl.formatMessage({ id: `${prefix}.filter.ALL` }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.YES` }),
      value: 'yes',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.NO` }),
      value: 'no',
    },
  ];

  const toggleWarranty = [
    {
      text: intl.formatMessage({ id: `${prefix}.filter.ALL` }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.YES` }),
      value: 'yes',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.filter.NO` }),
      value: 'no',
    },
  ];

  const sorts = [
    {
      text: intl.formatMessage({ id: `${prefix}.sort.BUNDLE` }),
      value: 'bundle',
    },
    {
      text: intl.formatMessage({ id: `${prefix}.sort.DESCRIPTION` }),
      value: 'name',
    },
  ];

  const getColumns = () => {
    const { formatMessage: translate } = intl;

    return [
      {
        displayName: translate({ id: `${prefix}.BUNDLE_COL` }),
        fieldName: 'bundle',
        width: '60px',
      },
      {
        displayName: translate({ id: `${prefix}.LEADER_COL` }),
        fieldName: 'isLeader',
        width: '60px',
      },
      {
        displayName: translate({ id: `${prefix}.SERIAL_COL` }),
        fieldName: 'serialNumber',
        width: '160px',
      },
      {
        displayName: translate({ id: `${prefix}.DESCRIPTION_COL` }),
        fieldName: 'name',
        width: '300px',
        tooltip: row => row.name,
      },
      {
        displayName: translate({ id: `${prefix}.WORKCENTER_COL` }),
        fieldName: 'mainWorkCenterName',
        width: '250px',
      },
      {
        displayName: translate({ id: `${prefix}.STATUS_COL` }),
        renderFn: (value) => {
          let label = value?.status;
          if (!label) {
            label = 'Unknown';
          }
          return translate({ id: `equipment.status.${label}` });
        },
        width: '180px',
      },
      {
        displayName: translate({ id: `${prefix}.AGE_COL` }),
        fieldName: 'ageFormatted',
        width: '60px',
      },
      {
        displayName: translate({ id: `${prefix}.WARRANTYEND_COL` }),
        fieldName: 'warrantyEndDate',
        renderFn: v => v?.warrantyEndDate && intl.formatDate(v?.warrantyEndDate),
        width: '100px',
      },
      {
        displayName: translate({ id: `${prefix}.CONTRACT_COL` }),
        fieldName: 'contractIndicator',
        width: '65px',
      },
      {
        displayName: translate({ id: `${prefix}.ACT_COL` }),
        renderFn: v => `${v?.actualRunningHours} hrs`,
        width: '90px',
      },
      {
        displayName: translate({ id: `${prefix}.EST_YR_COL` }),
        renderFn: v => `${v?.estimatedRunningHoursPerYear} hrs`,
        width: '90px',
      },
      {
        displayName: translate({ id: `${prefix}.ACT_YR_COL` }),
        renderFn: v => `${v?.actualRunningHoursPerYear} hrs`,
        width: '90px',
      },
      {
        renderFn: v =>
          <>
            {v.hasAlert && <AlertIcon />}
            {v.hasTicket && <TicketIcon />}
          </>
        ,
      },
    ];
  };

  const getFilteredAndSortedData = () => equipments
    ?.filter((eq) => {
      switch (toggleContractValue) {
        case 'yes':
          return !['NO', 'ENDED'].includes(eq.contractIndicator);
        case 'no':
          return ['NO', 'ENDED'].includes(eq.contractIndicator);
        default:
          return true;
      }
    })
    ?.filter(
      eq =>
        toggleCompetitorValue === 'all'
          ? true
          : eq.isCompetitor &&
          eq.isCompetitor?.toLowerCase() === toggleCompetitorValue,
    )
    ?.filter(
      eq =>
        toggleWarrantyValue === 'all' ||
        (toggleWarrantyValue === 'yes' &&
          eq.warrantyIndicator !== 'NO' &&
          eq.warrantyIndicator !== 'ENDED') ||
        (toggleWarrantyValue === 'no' &&
          (eq.warrantyIndicator === 'NO' ||
            eq.warrantyIndicator === 'ENDED')),
    )
    ?.sort((prevEq, currEq) => {
      if (prevEq[sortOrder.order] === null) {
        return 1;
      } else if (currEq[sortOrder.order] === null) {
        return -1;
      } else if (prevEq[sortOrder.order] === currEq[sortOrder.order]) {
        return 0;
      } else if (sortOrder.direction === 'ASC') {
        return prevEq[sortOrder.order]
          .trim()
          .localeCompare(
            currEq[sortOrder.order].trim(),
            {},
            { numeric: true, sensitivity: 'base' },
          );
      }
      return currEq[sortOrder.order]
        .trim()
        .localeCompare(
          prevEq[sortOrder.order].trim(),
          {},
          { numeric: true, sensitivity: 'base' },
        );
    });

  const handleRowSelect = (v) => {
    if (selectedEquipments.find(e => e === v)) {
      setSelectedEquipments(s => s.filter(f => f !== v));
    } else {
      setSelectedEquipments(s => [...s, v]);
    }
  };

  const handleRowClick = (id) => {
    history.push(`/equipment/${id}`, { context: id });
  };

  const handleContractChange = e => setToggleContractValue(e.target.value);
  const handleCompetitorChange = e => setToggleCompetitorValue(e.target.value);
  const handleWarrantyChange = e => setToggleWarrantyValue(e.target.value);
  const handleSort = (newSortOrder) => {
    localStorage.setItem('equipmentTable-sort', JSON.stringify(newSortOrder));
    setSortOrder(newSortOrder);
  };

  const handleCreateOrderClick = () => {
    history.push(`create-order/equipment/${selectedEquipments.join(',')}`);
  };

  const renderToolbar = () => {
    const { formatMessage } = intl;

    return (
      <Toolbar>
        <Toolbar.Item showAsQuickAccess={true}>
          <PermissionCheck permissionsList={['ordercreate']}>
            <Button
              type='secondary'
              dark={true}
              activeColor='#007AA4'
              onClick={handleCreateOrderClick}
              disabled={!selectedEquipments.length}
            >
              <FormattedMessage id='dashboard.CREATE_ORDER_BTN' />
            </Button>
          </PermissionCheck>
        </Toolbar.Item>
        <Toolbar.Item showAsQuickAccess={true}>
          <ToggleFilter
            value={toggleContractValue}
            items={toggleContract}
            dark={true}
            activeColor='#007AA4'
            label={formatMessage({ id: `${prefix}.toolbar.CONTRACT_LABEL` })}
            onChange={handleContractChange}
          />
        </Toolbar.Item>
        <Toolbar.Item showAsQuickAccess={true}>
          <ToggleFilter
            value={toggleCompetitorValue}
            items={toggleCompetitor}
            dark={true}
            activeColor='#007AA4'
            label={formatMessage({ id: `${prefix}.toolbar.COMPETITOR_LABEL` })}
            onChange={handleCompetitorChange}
          />
        </Toolbar.Item>
        <Toolbar.Item showAsQuickAccess={true}>
          <ToggleFilter
            value={toggleWarrantyValue}
            items={toggleWarranty}
            dark={true}
            activeColor='#007AA4'
            label={formatMessage({ id: `${prefix}.toolbar.WARRANTY_LABEL` })}
            onChange={handleWarrantyChange}
          />
        </Toolbar.Item>
        <Toolbar.Item showAsQuickAccess={true}>
          <ToggleSort
            value={sortOrder}
            items={sorts}
            dark={true}
            activeColor='#007AA4'
            label={formatMessage({ id: `${prefix}.toolbar.SORT_LABEL` })}
            onChange={handleSort}
          />
        </Toolbar.Item>
      </Toolbar>
    );
  };

  const renderHeader = () => <ThemeHeader inlineTable={true}>
    <span>
      {`${intl.formatMessage({ id: `${prefix}.TABLE_TITLE` })} `}
      ({equipments?.length})
    </span>
    {renderToolbar()}
  </ThemeHeader>;

  const filteredAndSortedEquipments = getFilteredAndSortedData();

  return (
    <Wrapper>
      <Table
        renderCustomHeader={renderHeader}
        usePaging={true}
        fullWidth={true}
        columns={getColumns()}
        data={filteredAndSortedEquipments}
        isLoading={isLoading}
        selectedRowIds={selectedEquipments}
        onRowSelect={handleRowSelect}
        onRowClick={handleRowClick}
        activeRow={activeRow}
        lightHeader={true}
      />
    </Wrapper>
  );
};

EquipmentsTable.propTypes = {
  isLoading: PropTypes.bool,
  equipments: PropTypes.array,
  intl: PropTypes.object,
  history: PropTypes.object.isRequired,
  activeRow: PropTypes.object,
};

export default withRouter(injectIntl(EquipmentsTable));
